import React, { useEffect, useState } from "react";
import Layout from "../Partials/Layout";
import ModalCom from "../Helpers/ModalCom";
import usersService from "../../services/UsersService";
import {
  formatDuration,
  nextOccurrence,
  toApiDateTime,
  toDatetimeLocalValue,
} from "../../utils/eventDates";

// Same capabilities as myfit-app's Events feature (EventsListScreen/
// AddEventScreen/EventDetailScreen), against the same backend (myfit-api's
// /myevents) — replaces the old "Calendar" page, which plotted vendor
// desktop-API reminders (getUserReminders(), depending on `member_id`, a
// value the passwordless myfit-api login has no equivalent for — see
// Login/index.jsx) onto a react-calendar grid.
const MAX_EVENTS = 30;
const DURATION_PRESETS = [15, 30, 45, 60];
const REPEAT_OPTIONS = [
  { id: "one_time", label: "One time" },
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
];

function nextRoundHour() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
}

function formatWhen(date) {
  return `${date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  })} · ${date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

function AddEventModal({ family, onClose, onSaved }) {
  const api = new usersService();
  const [name, setName] = useState("");
  const [when, setWhen] = useState(toDatetimeLocalValue(nextRoundHour()));
  const [duration, setDuration] = useState(30);
  const [customDuration, setCustomDuration] = useState("");
  const [repeat, setRepeat] = useState("one_time");
  const [forFamilyMemberId, setForFamilyMemberId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectPreset = (minutes) => {
    setDuration(minutes);
    setCustomDuration("");
  };

  const onCustomDurationChange = (value) => {
    const digits = value.replace(/[^0-9]/g, "");
    setCustomDuration(digits);
    const n = parseInt(digits, 10);
    if (!Number.isNaN(n) && n > 0) setDuration(n);
  };

  const onSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Enter a name for this event.");
      return;
    }
    if (!duration || duration <= 0) {
      setError("Enter a valid duration.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res = await api.addEvent({
        name: trimmed,
        date_time: toApiDateTime(when),
        duration_minutes: duration,
        repeat,
        family_member_id: forFamilyMemberId,
      });
      onSaved(res.data.event);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save this event.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="logout-modal-wrapper lg:w-[520px] h-full lg:max-h-[90vh] overflow-y-auto bg-white dark:bg-dark-white   lg:rounded-2xl">
      <div className="logout-modal-header w-full flex items-center justify-between lg:px-10 lg:py-8 px-[30px] py-[23px] border-b border-light-purple dark:border-[#5356fb29] ">
        <h1 className="text-26 font-bold text-dark-gray dark:text-white tracking-wide">
          Add Event
        </h1>
        <button
          type="button"
          onClick={onClose}
          className="text-3xl leading-none text-thin-light-gray"
        >
          ×
        </button>
      </div>
      <div className="logout-modal-body w-full px-10 py-8">
        <label className="text-sm text-thin-light-gray block mb-2" htmlFor="event-name">
          Name
        </label>
        <input
          id="event-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
          placeholder="e.g. Take Medication"
          className="w-full border border-light-purple dark:border-[#5356fb29] rounded-full px-5 py-3 bg-[#FAFAFA] dark:bg-[#11131F] text-dark-gray dark:text-white mb-6 focus:ring-0 focus:outline-none"
        />

        <p className="text-sm text-thin-light-gray mb-2">For</p>
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            type="button"
            onClick={() => setForFamilyMemberId(null)}
            disabled={saving}
            className={`px-4 py-2 rounded-full border text-sm font-semibold ${
              forFamilyMemberId === null
                ? "bg-purple text-white border-purple"
                : "border-light-purple dark:border-[#5356fb29] text-dark-gray dark:text-white"
            }`}
          >
            Self
          </button>
          {family.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setForFamilyMemberId(m.id)}
              disabled={saving}
              className={`px-4 py-2 rounded-full border text-sm font-semibold ${
                forFamilyMemberId === m.id
                  ? "bg-purple text-white border-purple"
                  : "border-light-purple dark:border-[#5356fb29] text-dark-gray dark:text-white"
              }`}
            >
              {m.first_name}
            </button>
          ))}
        </div>

        <p className="text-sm text-thin-light-gray mb-2">Date &amp; time</p>
        <input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          disabled={saving}
          className="w-full border border-light-purple dark:border-[#5356fb29] rounded-full px-5 py-3 bg-[#FAFAFA] dark:bg-[#11131F] text-dark-gray dark:text-white mb-6 focus:ring-0 focus:outline-none"
        />

        <p className="text-sm text-thin-light-gray mb-2">Duration</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {DURATION_PRESETS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => selectPreset(m)}
              disabled={saving}
              className={`px-4 py-2 rounded-full border text-sm font-semibold ${
                duration === m && !customDuration
                  ? "bg-purple text-white border-purple"
                  : "border-light-purple dark:border-[#5356fb29] text-dark-gray dark:text-white"
              }`}
            >
              {m}m
            </button>
          ))}
          <input
            type="text"
            inputMode="numeric"
            value={customDuration}
            onChange={(e) => onCustomDurationChange(e.target.value)}
            disabled={saving}
            placeholder="Custom"
            className={`w-24 px-4 py-2 rounded-full border text-sm font-semibold text-center bg-[#FAFAFA] dark:bg-[#11131F] focus:ring-0 focus:outline-none ${
              customDuration
                ? "border-purple text-purple"
                : "border-light-purple dark:border-[#5356fb29] text-dark-gray dark:text-white"
            }`}
          />
        </div>

        <p className="text-sm text-thin-light-gray mb-2">Repeat</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {REPEAT_OPTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRepeat(r.id)}
              disabled={saving}
              className={`px-4 py-2 rounded-full border text-sm font-semibold ${
                repeat === r.id
                  ? "bg-purple text-white border-purple"
                  : "border-light-purple dark:border-[#5356fb29] text-dark-gray dark:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {error && <p className="text-center text-red-700 mb-4">{error}</p>}

        <div className="flex justify-center">
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="btn-login rounded-[50px] text-xl text-white font-bold px-10 py-3 bg-purple"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EventDetailModal({ event, onClose, onDeleted }) {
  const api = new usersService();
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState("");

  const when = new Date(event.date_time);
  const repeatLabel =
    REPEAT_OPTIONS.find((r) => r.id === event.repeat)?.label || event.repeat;

  const rows = [
    {
      label: "Date & time",
      value: `${when.toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })} · ${when.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`,
    },
    { label: "Duration", value: formatDuration(event.duration_minutes) },
    { label: "Repeat", value: repeatLabel },
    { label: "For", value: event.family_member_name || "Self" },
  ];

  const onDelete = async () => {
    setError("");
    setDeleting(true);
    try {
      await api.deleteEvent(event.id);
      onDeleted(event.id);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not delete this event.");
      setDeleting(false);
    }
  };

  return (
    <div className="logout-modal-wrapper lg:w-[520px] h-full lg:max-h-[90vh] overflow-y-auto bg-white dark:bg-dark-white   lg:rounded-2xl">
      <div className="logout-modal-header w-full flex items-center justify-between lg:px-10 lg:py-8 px-[30px] py-[23px] border-b border-light-purple dark:border-[#5356fb29] ">
        <h1 className="text-26 font-bold text-dark-gray dark:text-white tracking-wide">
          {event.name}
        </h1>
        <button
          type="button"
          onClick={onClose}
          className="text-3xl leading-none text-thin-light-gray"
        >
          ×
        </button>
      </div>
      <div className="logout-modal-body w-full px-10 py-8">
        <div className="mb-6">
          {rows.map((row, i) => (
            <div
              key={row.label}
              className={`flex justify-between py-3 ${
                i === rows.length - 1 ? "" : "border-b border-light-purple dark:border-[#5356fb29]"
              }`}
            >
              <span className="text-thin-light-gray">{row.label}</span>
              <span className="text-dark-gray dark:text-white font-medium">{row.value}</span>
            </div>
          ))}
        </div>

        {error && <p className="text-center text-red-700 mb-4">{error}</p>}

        {confirmingDelete ? (
          <div className="flex justify-center space-x-3">
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="text-white bg-light-red text-base font-bold px-6 py-3 rounded-full"
            >
              {deleting ? "Deleting…" : "Yes, Delete"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              disabled={deleting}
              className="border-gradient text-base px-6 py-3 rounded-full"
            >
              <span className="text-gradient">Cancel</span>
            </button>
          </div>
        ) : (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="text-white bg-light-red text-base font-bold px-6 py-3 rounded-full"
            >
              Delete Event
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Events() {
  const api = new usersService();

  const [rows, setRows] = useState([]); // [{ event, when }], upcoming, soonest first
  const [family, setFamily] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAddModal, setShowAddModal] = useState(false);
  const [detailEvent, setDetailEvent] = useState(null);

  const load = () => {
    setLoading(true);
    setError("");
    Promise.all([api.fetchEvents(), api.fetchFamily()])
      .then(([eventsRes, familyRes]) => {
        const now = new Date();
        const upcoming = (eventsRes.data.events || [])
          .map((event) => {
            const when = nextOccurrence(event, now);
            return when ? { event, when } : null;
          })
          .filter((r) => r !== null)
          .sort((a, b) => a.when.getTime() - b.when.getTime())
          .slice(0, MAX_EVENTS);
        setRows(upcoming);
        setFamily(familyRes.data.family || []);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Could not load your events.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Layout>
        <div className="events-wrapper w-full mb-10">
          <div className="main-wrapper w-full">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-26 font-bold text-dark-gray dark:text-white">Events</h1>
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="rounded-[50px] text-base text-white font-bold px-6 py-3 bg-purple"
              >
                + Add Event
              </button>
            </div>

            {error && <p className="text-center text-red-700 mb-4">{error}</p>}

            <div className="update-table w-full p-8 bg-white dark:bg-dark-white   overflow-hidden rounded-2xl section-shadow min-h-[300px]">
              {loading ? (
                <p className="text-center text-thin-light-gray py-16">Loading…</p>
              ) : rows.length === 0 ? (
                <div className="flex flex-col items-center py-16">
                  <p className="text-thin-light-gray mb-6">No upcoming events.</p>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(true)}
                    className="btn-login rounded-[50px] text-white font-bold px-8 py-3 bg-purple"
                  >
                    Get Started
                  </button>
                </div>
              ) : (
                <div className="relative w-full overflow-x-auto sm:rounded-lg">
                  <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                    <tbody>
                      <tr className="text-base text-thin-light-gray whitespace-nowrap border-b dark:border-[#5356fb29]">
                        <td className="py-4">When</td>
                        <td className="py-4">Event</td>
                        <td className="py-4 text-right">For</td>
                      </tr>
                      {rows.map(({ event, when }) => (
                        <tr
                          key={event.id}
                          onClick={() => setDetailEvent(event)}
                          className="cursor-pointer bg-white dark:bg-dark-white   border-b dark:border-[#5356fb29]  hover:bg-gray-50"
                        >
                          <td className="py-4 whitespace-nowrap">
                            <span className="text-base text-purple font-bold">
                              {formatWhen(when)}
                            </span>
                          </td>
                          <td className="py-4">
                            <h1 className="font-bold text-lg text-dark-gray dark:text-white whitespace-nowrap">
                              {event.name}
                            </h1>
                            <span className="text-sm text-thin-light-gray">
                              {formatDuration(event.duration_minutes)} ·{" "}
                              {REPEAT_OPTIONS.find((r) => r.id === event.repeat)?.label ||
                                event.repeat}
                            </span>
                          </td>
                          <td className="text-right py-4 px-2">
                            <span className="text-base text-dark-gray dark:text-white font-medium whitespace-nowrap">
                              {event.family_member_name || "Self"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>

      {showAddModal && (
        <ModalCom action={() => setShowAddModal(false)} situation={showAddModal}>
          <AddEventModal
            family={family}
            onClose={() => setShowAddModal(false)}
            onSaved={() => {
              setShowAddModal(false);
              load();
            }}
          />
        </ModalCom>
      )}

      {detailEvent && (
        <ModalCom action={() => setDetailEvent(null)} situation={!!detailEvent}>
          <EventDetailModal
            event={detailEvent}
            onClose={() => setDetailEvent(null)}
            onDeleted={() => {
              setDetailEvent(null);
              load();
            }}
          />
        </ModalCom>
      )}
    </>
  );
}
